import { ReactNode, useEffect, useRef, useState, forwardRef } from "react";
import styles from "./styles/Chip.module.css";
import { useFocusHandling } from "../../../hooks/useFocusHandling";
import {
  LabelPairedCircleXmarkSmRegularIcon,
  StandaloneChevronDownBoldIcon,
} from "@deriv/quill-icons";

import { useKeyboardAction } from "../../../hooks/useKeyboardAction";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { useActionSheetDetection } from "../../../hooks/useActionSheetDetection";
import {
  DropdownContainerAtom,
  DropdownDataType,
  DropdownItemType,
} from "../../../atoms/dropdown/container";

export type ChipBaseType = {
  className?: string;
  counter?: string;
  label?: string;
  labelTag?: string;

  /** Variant props */
  itemSelected?: boolean;
  size?: "sm" | "md" | "lg";
  state?: "default" | "expand" | "disabled";

  /** Custom props */
  icon?: ReactNode;
  selected?: DropdownItemType | DropdownItemType[];
  type?: "multiSelect" | "singleSelect";
  variant?: "dropdown" | "dismissable" | "selectable";
  onClick?: () => void;
  onChange?: (item: DropdownItemType | DropdownItemType[]) => void;
  options?: DropdownDataType[];
};

export interface ChipDropdownRef {
  updateItems: (newItems: DropdownDataType[]) => void;
}

const ChipBase = forwardRef<ChipDropdownRef, ChipBaseType>(
  (
    {
      className = "",
      itemSelected = false,
      size = "sm",
      state = "default",
      label = "Label",
      labelTag,
      variant = "dropdown",
      type = "singleSelect",
      icon,
      onClick,
      onChange,
      selected: selectedItems = [],
      options: initialItems = [],
    },
    ref
  ) => {
    const [highLight, setHighLight] = useState(false);
    const [internalState, setInternalState] = useState(state);
    const [isSelected, setIsSelected] = useState(itemSelected);
    const [isExpanded, setIsExpanded] = useState(state === "expand");
    const [items, setItems] = useState(initialItems);
    const [selected, setSelected] = useState(selectedItems);

    const itemSizes: Record<NonNullable<ChipBaseType["size"]>, "sm" | "md"> = {
      sm: "sm",
      md: "md",
      lg: "md",
    };

    const itemSize = itemSizes[size];

    const chipRef = useRef<HTMLDivElement>(null);
    const ChipId = `quill-chip-dropdown-${variant}-${state}-${size}-${
      isSelected ? "selected" : "unselected"
    }`;

    const { handleFocus, handleBlur } = useFocusHandling({
      isDisabled: state === "disabled",
      setHighlight: setHighLight,
    });

    const { isInsideActionSheet } = useActionSheetDetection();

    /** Collapse Handler */
    const handleClick = (event?: React.MouseEvent) => {
      if (state === "disabled" || variant === "dismissable") return;

      if (event) {
        const target = event.target as HTMLElement;

        // Use the hook to check if the click is inside an action sheet
        if (isInsideActionSheet(target)) {
          return;
        }
      }

      const target = event?.target as HTMLElement;
      const isClickedOnDropdown = target?.closest(
        ".quill-ui-next-dropdown-container"
      );

      const shouldBehaveLikeSingleSelect =
        type !== "multiSelect" || !isClickedOnDropdown;
      const isExpanding = shouldBehaveLikeSingleSelect
        ? internalState !== "expand"
        : true;


      const isSelected = variant === "selectable" ? true : isExpanding;
     

      setInternalState(isExpanding ? "expand" : "default");
      setIsSelected(isSelected);

      onClick?.();
    };

    const handlePress = useKeyboardAction(handleClick);

    /** Uncollapse Handler */
    const handleClickOutside = () => {
      if (state === "disabled" || variant !== "dropdown") return;

      setIsSelected(false);
    };

    useClickOutside(chipRef, handleClickOutside);

    useEffect(() => {
      setInternalState(state);
    }, [state]);

    useEffect(() => {
      setIsExpanded(internalState === "expand");
    }, [internalState]);

    useEffect(() => {
      setIsSelected(itemSelected);
    }, [itemSelected]);

    return (
      <div
        ref={chipRef}
        className={[
          styles.root,
          "quill-ui-next-component",
          "quill-ui-next-no-select",
          state !== "disabled" && "quill-ui-next-pointer",
          className,
        ].join(" ")}
        data-id={ChipId}
        data-itemSelected={isSelected}
        data-size={size}
        data-state={internalState}
        tabIndex={state === "disabled" ? -1 : 0}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
        onKeyDown={handlePress}
      >
        <div className={styles.chip}>
          {icon && (
            <div className={`${styles.iconLeft} chip-dropdown-icon`}>
              {icon && icon}
            </div>
          )}
          <div className={styles.label}>
            {type === "singleSelect" && selected && !Array.isArray(selected)
              ? selected?.label || label
              : label}
          </div>
          {labelTag && <b className={styles.labelTag}>{labelTag}</b>}
          {variant === "dropdown" &&
            type === "multiSelect" &&
            Array.isArray(selected) &&
            selected.length > 0 && (
              <div className={styles.counterLabel}>({selected.length})</div>
            )}

          {variant === "dropdown" && (
            <div className={`${styles.iconChevron} chip-dropdown-icon`}>
              <StandaloneChevronDownBoldIcon
                className={`quill-ui-next-rotatable ${
                  isExpanded && "quill-ui-next-rotate"
                }`}
                iconSize="sm"
              />
            </div>
          )}

          {variant === "dismissable" && (
            <LabelPairedCircleXmarkSmRegularIcon className={styles.iconClose} />
          )}

          {highLight && <div className={styles.bgFocus} />}
        </div>

        {variant === "dropdown" && (
          <DropdownContainerAtom
            offsetTop={5}
            parent={chipRef}
            isOpen={isExpanded}
            data={items}
            multiple={type === "multiSelect"}
            containerSize={size}
            itemSize={itemSize}
            selected={selected}
            onChange={(e) => {
              onChange?.(e);
              setSelected(e);
              // Close the dropdown for single select after selection
              if (type === "singleSelect") {
                setInternalState("default");
                setIsSelected(false);
              }
            }}
            onActionSheetClose={handleClickOutside}
          />
        )}
      </div>
    );
  }
);

export default ChipBase;
